// client/src/components/ProjectScorecard.js
// Project-level KPI scorecard — the landing page when a user clicks a project.
// Fetches GET /api/projects/:id/scorecard and renders aggregate tiles across
// all campaigns in the project.
//
// Layout (FINAL Aug 2026):
//   No header (SidebarLayout provides icon roundel + project name at top)
//   Row 1 — Two-column: Project stats (L) | Performance (R)
//   Row 2 — Two-column: Campaigns list with scrollbar (L) | Trends chart (R)
//   Row 3 — Two-column: Visibility by Engine (L, half-width) | Project access (R, half-width)
//
// UPDATED: Campaigns list now shows Live/Paused/Archived status with toggle controls
// UPDATED Aug 2026: Campaigns list and trends chart now share same height (450px)
// UPDATED Aug 24 2026: Added "+ New campaign" button — routes to the campaign builder at
//                      /project/:projectId/campaign-builder, which was already registered in
//                      App.js but had no entry point anywhere in the UI. The button renders in
//                      both the populated and empty states (the empty state previously told
//                      users to "build your first campaign" without offering a way to do it).
//                      The list and empty state are now a single ternary rather than two
//                      independent && blocks keyed on different values (campaigns.length vs
//                      s.totalCampaigns) — if those ever disagreed, neither rendered and the
//                      column went blank.
// UPDATED Aug 24 2026: Changed grid layout from 4 columns to 5 columns for Project and
//                      Performance tiles while maintaining 160px tile width.
//
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import ProjectMembersPanel from './ProjectMembersPanel';
import { DeltaBadge } from './DeltaBadge';
const C = {
  primary:   '#39393a',
  secondary: '#048ba8',
  tertiary:  '#e9c46a',
  grey:      '#e76f51',
  green:     '#01796f',
  textMuted: '#6b7280',
  border:    '#e5e7eb',
  lightbg:   '#f9fafb',
  green_bg:  'rgba(1, 121, 111, 0.12)',
  amber_bg:  'rgba(233, 196, 106, 0.12)',
  red_bg:    'rgba(231, 111, 81, 0.12)',
  green_tx:  '#01796f',
  amber_tx:  '#e9c46a',
  red_tx:    '#e76f51',
};
const CHART = {
  visibility: '#048ba8',
  citation:   '#01796f',
  errorRate:  '#e76f51',
  sentiment:  '#e9c46a',
};
const ENGINE_COLORS = {
  chatgpt:    '#10a37f',
  claude:     '#c084fc',
  gemini:     '#4285f4',
  perplexity: '#f59e0b',
};
// Status configuration for badges
const STATUS_CONFIG = {
  LIVE:     { label: 'Live',     bg: '#E0F5F3', text: '#01796f', dot: '#01796f' },
  PAUSED:   { label: 'Paused',   bg: '#FEF3C7', text: '#b45309', dot: '#f59e0b' },
  ARCHIVED: { label: 'Archived', bg: '#F3F4F6', text: '#6b7280', dot: '#9ca3af' },
};
function getInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  return words.length > 1
    ? (words[0][0] + words[words.length - 1][0]).toUpperCase()
    : name.substring(0, 2).toUpperCase();
}
function scoreColor(val, invert = false) {
  const good   = invert ? val <= 2  : val >= 70;
  const medium = invert ? val <= 5  : val >= 40;
  if (good)   return { tx: C.green_tx, bg: C.green_bg };
  if (medium) return { tx: C.amber_tx, bg: C.amber_bg };
  return       { tx: C.red_tx,   bg: C.red_bg };
}
function getScoreColor(score, type = 'default') {
  if (score === null) return C.primary;
  if (type === 'position') {
    if (score <= 2) return C.green;
    if (score <= 5) return C.secondary;
    if (score <= 8) return C.tertiary;
    return C.grey;
  }
  if (score >= 70) return C.green;
  if (score >= 45) return C.tertiary;
  return C.grey;
}
function formatMetricValue(value, type) {
  if (value === null) return 'N/A';
  switch (type) {
    case 'visibility':
    case 'citation':
    case 'content':
    case 'technical':
    case 'entityGap':
    case 'knowledgeGraph':
      return `${Math.round(value)}%`;
    case 'position':
      return value.toFixed(1);
    default:
      return value;
  }
}
function Tile({ label, value, suffix = '', invert = false, isCount = false, onClick }) {
  const isNull = value === null || value === undefined;
  let colorStyle = { tx: C.textMuted, bg: 'white' };
  if (!isNull && !isCount) colorStyle = scoreColor(value, invert);
  const displayValue = isNull
    ? '—'
    : isCount
      ? value.toLocaleString()
      : `${Math.round(value)}${suffix}`;
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: isCount ? 'white' : colorStyle.bg,
        border: `1px solid ${C.border}`,
        borderRadius: '10px',
        padding: '24px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s',
        width: '160px',
        height: '140px',
        justifyContent: 'center',
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ fontSize: '11px', fontWeight: '600', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.7px' }}>
        {label}
      </div>
      <div style={{
        fontSize: isCount ? '32px' : '40px',
        fontWeight: '800',
        color: isCount ? C.primary : (isNull ? '#d1d5db' : colorStyle.tx),
        lineHeight: 1,
      }}>
        {displayValue}
      </div>
    </div>
  );
}
function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: '11px',
      fontWeight: '700',
      color: C.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      marginTop: '32px',
      marginBottom: '12px',
      paddingBottom: '8px',
      borderBottom: `1px solid ${C.border}`,
    }}>
      {children}
    </div>
  );
}
// Status badge component
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.LIVE;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      fontSize: '11px', fontWeight: '600', color: cfg.text,
      backgroundColor: cfg.bg, padding: '2px 8px',
      borderRadius: '9999px', whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        backgroundColor: cfg.dot, flexShrink: 0,
      }} />
      {cfg.label}
    </span>
  );
}
// Status toggle component
function StatusToggle({ current, onSelect, updating }) {
  const statuses = ['LIVE', 'PAUSED', 'ARCHIVED'];
  return (
    <div
      style={{
        display: 'inline-flex', borderRadius: '6px',
        border: `1px solid ${C.border}`, overflow: 'hidden',
        opacity: updating ? 0.5 : 1, transition: 'opacity 0.15s',
      }}
      onClick={e => e.stopPropagation()}
    >
      {statuses.map((s, i) => {
        const isActive = (current || 'LIVE') === s;
        const cfg = STATUS_CONFIG[s];
        return (
          <button
            key={s}
            disabled={updating}
            onClick={() => !isActive && onSelect(s)}
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: '600',
              border: 'none',
              borderRight: i < statuses.length - 1 ? `1px solid ${C.border}` : 'none',
              backgroundColor: isActive ? cfg.bg : 'white',
              color: isActive ? cfg.text : C.textMuted,
              cursor: isActive || updating ? 'default' : 'pointer',
              transition: 'all 0.15s',
              fontFamily: "'Work Sans', sans-serif",
            }}
          >
            {cfg.label}
          </button>
        );
      })}
    </div>
  );
}
// Updated campaign row with status and controls
function CampaignRow({ campaign, onClick, apiBaseUrl, userId, onStatusChange }) {
  const [updating, setUpdating] = useState(false);
  const [hovered, setHovered] = useState(false);
  // Determine current status: prefer campaign_status field, fall back to archived boolean
  const currentStatus = campaign.campaign_status ||
    (campaign.archived ? 'ARCHIVED' : 'LIVE');
  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const res = await fetch(`${apiBaseUrl}/campaigns/${campaign.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      // Notify parent to update campaigns list
      if (onStatusChange) {
        onStatusChange(campaign.id, newStatus);
      }
    } catch (err) {
      alert(`Could not update status: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 100px 130px 80px',
        alignItems: 'center',
        padding: '12px 20px',
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: hovered ? C.lightbg : 'transparent',
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        gap: '12px',
      }}
    >
      <div onClick={onClick} style={{ cursor: 'pointer' }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '600',
          color: C.primary,
        }}>
          {campaign.name}
        </div>
        {campaign.brand_name && (
          <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '2px' }}>
            {campaign.brand_name}
          </div>
        )}
      </div>
      <div>
        <StatusBadge status={currentStatus} />
      </div>
      <div style={{
        opacity: hovered ? 1 : 0,
        transition: 'opacity 0.15s',
      }}>
        <StatusToggle
          current={currentStatus}
          onSelect={handleStatusChange}
          updating={updating}
        />
      </div>
      <div style={{ fontSize: '12px', color: C.textMuted, textAlign: 'right' }}>
        {campaign.urls?.length || 0} URL{campaign.urls?.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
function TimelineTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      backgroundColor: 'white',
      border: `1px solid ${C.border}`,
      borderRadius: '8px',
      padding: '10px 14px',
      fontSize: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div style={{ fontWeight: '700', color: C.primary, marginBottom: '6px' }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color, display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontWeight: '600' }}>{p.name}:</span>
          <span>{p.value !== null && p.value !== undefined ? `${Math.round(p.value)}%` : '—'}</span>
        </div>
      ))}
    </div>
  );
}
function EngineTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      backgroundColor: 'white',
      border: `1px solid ${C.border}`,
      borderRadius: '8px',
      padding: '10px 14px',
      fontSize: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div style={{ fontWeight: '700', color: C.primary, marginBottom: '4px', textTransform: 'capitalize' }}>{label}</div>
      <div style={{ color: payload[0].color, fontWeight: '600' }}>
        Visibility: {payload[0].value}%
      </div>
    </div>
  );
}
function extractRunMetrics(resultData, createdAt) {
  if (!resultData) return null;
  const summary = resultData.summary || {};
  const brandMetrics = Array.isArray(resultData.brandMetrics)
    ? resultData.brandMetrics
    : Array.isArray(summary.brandMetrics)
      ? summary.brandMetrics
      : [];
  const target = brandMetrics.find(b => b && b.isTarget);
  const visibility = target?.visibilityScore ?? summary.brandMentionRate ?? null;
  const citation   = typeof resultData.citationRate === 'number'
    ? resultData.citationRate
    : target?.citationScore ?? summary.citationRate ?? null;
  const sentiment  = (target && Array.isArray(target.sentiments) && target.sentiments.length > 0)
    ? target.averageSentiment
    : typeof summary.sentimentScore === 'number'
      ? summary.sentimentScore
      : null;
  const ep = summary.enginePerformance || {};
  const engines = {};
  for (const [engine, data] of Object.entries(ep)) {
    engines[engine] = typeof data.brandMentionRate === 'number' ? data.brandMentionRate : null;
  }
  const date = new Date(createdAt);
  const label = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  return { date: createdAt, label, visibility, citation, sentiment, engines };
}
function ProjectScorecard({ apiBaseUrl, userId }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const clerkUserId = user?.id || userId;
  const [scorecard, setScorecard]           = useState(null);
  const [project, setProject]               = useState(null);
  const [campaigns, setCampaigns]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [timelineData, setTimelineData]     = useState([]);
  const [engineData, setEngineData]         = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isAdmin, setIsAdmin]               = useState(false);
  const [deltas, setDeltas]                 = useState({});
  // Shared container height for campaigns list and trends chart
  const containerHeight = '450px';
  useEffect(() => {
    if (!projectId || !apiBaseUrl) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${apiBaseUrl}/projects/${projectId}/scorecard`, {
          headers: { 'x-user-id': clerkUserId || '' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'Failed to load scorecard');
        setProject(json.project);
        setScorecard(json.scorecard);
        if (json.scorecard.deltas) {
          setDeltas(json.scorecard.deltas);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId, apiBaseUrl, clerkUserId]);
  useEffect(() => {
    if (!projectId || !apiBaseUrl || !clerkUserId) return;
    const fetchCampaigns = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/campaigns`, {
          headers: { 'x-user-id': clerkUserId },
        });
        if (!res.ok) return;
        const data = await res.json();
        const allCampaigns = Array.isArray(data) ? data : (data.campaigns || []);
        const projectCampaigns = allCampaigns.filter(c => c.project_id === projectId);
        setCampaigns(projectCampaigns.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (_) {}
    };
    fetchCampaigns();
  }, [projectId, apiBaseUrl, clerkUserId]);
  useEffect(() => {
    if (!clerkUserId || !apiBaseUrl) return;
    const checkAdmin = async () => {
      try {
        const res  = await fetch(`${apiBaseUrl}/org/mine`, {
          headers: { 'x-user-id': clerkUserId }
        });
        const data = await res.json();
        if (!res.ok || !data.org) return;
        const isOwner = data.org.created_by_user_id === clerkUserId;
        const member  = (data.org.members || []).find(m => m.user_id === clerkUserId);
        setIsAdmin(isOwner || member?.role === 'admin');
      } catch (_) {}
    };
    checkAdmin();
  }, [clerkUserId, apiBaseUrl]);
  useEffect(() => {
    if (!scorecard || !apiBaseUrl || !clerkUserId) return;
    if (scorecard.totalCampaigns === 0) return;
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const campaignsRes = await fetch(`${apiBaseUrl}/campaigns`, {
          headers: { 'x-user-id': clerkUserId },
        });
        if (!campaignsRes.ok) return;
        const allCampaigns = await campaignsRes.json();
        const projectCampaigns = Array.isArray(allCampaigns)
          ? allCampaigns.filter(c => c.project_id === projectId)
          : [];
        if (!projectCampaigns.length) return;
        const allRuns = [];
        await Promise.all(projectCampaigns.map(async (campaign) => {
          try {
            const res = await fetch(
              `${apiBaseUrl}/audit-results/campaign/${campaign.id}/history?moduleType=llm-audit&days=60`,
              { headers: { 'x-user-id': clerkUserId } }
            );
            if (!res.ok) return;
            const rows = await res.json();
            if (!Array.isArray(rows)) return;
            rows.forEach(row => {
              const metrics = extractRunMetrics(row.result_data, row.created_at);
              if (metrics) allRuns.push(metrics);
            });
          } catch (_) {}
        }));
        if (!allRuns.length) return;
        allRuns.sort((a, b) => new Date(a.date) - new Date(b.date));
        const byLabel = {};
        allRuns.forEach(run => {
          if (!byLabel[run.label]) byLabel[run.label] = [];
          byLabel[run.label].push(run);
        });
        const timeline = Object.entries(byLabel).map(([label, runs]) => {
          const avg = (key) => {
            const vals = runs.map(r => r[key]).filter(v => v !== null && v !== undefined);
            return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
          };
          return {
            label,
            visibility: avg('visibility'),
            citation:   avg('citation'),
            sentiment:  avg('sentiment'),
          };
        });
        setTimelineData(timeline);
        const mostRecent = allRuns[allRuns.length - 1];
        if (mostRecent && mostRecent.engines) {
          const bars = Object.entries(mostRecent.engines)
            .filter(([, v]) => v !== null)
            .map(([engine, value]) => ({
              engine: engine.charAt(0).toUpperCase() + engine.slice(1),
              engineKey: engine,
              visibility: Math.round(value),
            }));
          setEngineData(bars);
        }
      } catch (_) {
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [scorecard, apiBaseUrl, clerkUserId, projectId]);
  const handleCampaignStatusChange = (campaignId, newStatus) => {
    setCampaigns(prev =>
      prev.map(c =>
        c.id === campaignId
          ? { ...c, campaign_status: newStatus, archived: newStatus === 'ARCHIVED' }
          : c
      )
    );
  };
  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: C.textMuted }}>
        <div style={{
          width: '32px', height: '32px',
          border: `3px solid ${C.border}`,
          borderTop: `3px solid ${C.secondary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px',
        }} />
        Loading project scorecard…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: C.red_tx }}>
        <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Failed to load scorecard</div>
        <div style={{ fontSize: '14px', color: C.textMuted }}>{error}</div>
      </div>
    );
  }
  if (!scorecard) return null;
  const s = scorecard;
  const hasChartData  = timelineData.length >= 2;
  const hasEngineData = engineData.length > 0;
  const initials = getInitials(project?.name);
  return (
    <div style={{ padding: '32px 32px' }}>
        {/* ── Project Stats (L) | Performance (R) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '40px' }}>
          <div>
            <SectionLabel>Project</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 160px)', gap: '12px' }}>
              <Tile label="Campaigns"      value={s.totalCampaigns} isCount />
              <Tile label="Campaigns Live" value={s.campaignsLive}  isCount />
              <Tile label="URLs"           value={s.totalUrls}      isCount />
              <Tile label="Prompts"        value={s.totalPrompts}   isCount />
              <Tile
                label="Entity Bank"
                value={s.entityBank}
                isCount
                onClick={() => navigate(`/project/${projectId}/discovery`)}
              />
            </div>
          </div>
          <div>
            <SectionLabel>Performance</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 160px)', gap: '12px' }}>
              <Tile label="Visibility"   value={s.visibility} suffix="%" />
              <Tile label="Citation"     value={s.citation}    suffix="%" />
              <Tile label="Avg Position" value={s.position}    suffix="" invert={true} />
              <Tile label="Error Rate"   value={s.errorPct}    suffix="%" invert={true} />
              <Tile label="KGIS"         value={s.kgis}        suffix="" />
            </div>
          </div>
        </div>
        {/* ── Campaigns List (L) | Trends Chart (R) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '40px' }}>
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: '16px',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SectionLabel>Campaigns · {campaigns.length}</SectionLabel>
              </div>
              <button
                onClick={() => navigate(`/project/${projectId}/campaign-builder`)}
                style={{
                  marginBottom: '12px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: C.secondary,
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  fontFamily: "'Work Sans', sans-serif",
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                + New campaign
              </button>
            </div>
            {campaigns.length > 0 ? (
              <div style={{
                backgroundColor: 'white',
                border: `1px solid ${C.border}`,
                borderRadius: '10px',
                overflow: 'hidden',
                height: containerHeight,
                overflowY: 'auto',
              }}>
                {campaigns.map((campaign, i) => (
                  <CampaignRow
                    key={campaign.id || i}
                    campaign={campaign}
                    onClick={() => navigate(`/campaign/${campaign.id}`)}
                    apiBaseUrl={apiBaseUrl}
                    userId={clerkUserId}
                    onStatusChange={handleCampaignStatusChange}
                  />
                ))}
              </div>
            ) : (
              <div style={{
                padding: '24px',
                backgroundColor: 'white',
                border: `1px solid ${C.border}`,
                borderRadius: '10px',
                textAlign: 'center',
                color: C.textMuted,
              }}>
                <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '8px', color: C.primary }}>
                  No campaigns yet
                </div>
                <div style={{ fontSize: '14px' }}>
                  Build your first campaign to see performance scores here.
                </div>
              </div>
            )}
          </div>
          {(hasChartData || historyLoading) && (
            <div>
              <SectionLabel>Trends – aggregate across all campaigns</SectionLabel>
              <div style={{
                backgroundColor: 'white',
                border: `1px solid ${C.border}`,
                borderRadius: '10px',
                padding: '24px',
                height: containerHeight,
                display: 'flex',
                flexDirection: 'column',
              }}>
                {historyLoading && !hasChartData ? (
                  <div style={{ textAlign: 'center', color: C.textMuted, fontSize: '13px', padding: '32px' }}>
                    Loading trend data…
                  </div>
                ) : (
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={timelineData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.textMuted }} tickLine={false} axisLine={{ stroke: C.border }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: C.textMuted }} tickLine={false} axisLine={false} tickFormatter={v => `${v}%`} width={36} />
                        <Tooltip content={<TimelineTooltip />} />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }} />
                        <Line type="monotone" dataKey="visibility" name="Visibility" stroke={CHART.visibility} strokeWidth={2} dot={{ r: 3, fill: CHART.visibility }} activeDot={{ r: 5 }} connectNulls />
                        <Line type="monotone" dataKey="citation" name="Citation" stroke={CHART.citation} strokeWidth={2} dot={{ r: 3, fill: CHART.citation }} activeDot={{ r: 5 }} connectNulls />
                        <Line type="monotone" dataKey="sentiment" name="Sentiment" stroke={CHART.sentiment} strokeWidth={2} dot={{ r: 3, fill: CHART.sentiment }} activeDot={{ r: 5 }} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* ── Visibility by Engine (L) | Project access (R) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
          {hasEngineData && (
            <div>
              <SectionLabel>Visibility by Engine</SectionLabel>
              <div style={{
                backgroundColor: 'white',
                border: `1px solid ${C.border}`,
                borderRadius: '10px',
                padding: '24px',
              }}>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={engineData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }} barSize={40}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                    <XAxis dataKey="engine" tick={{ fontSize: 12, fill: C.textMuted, fontWeight: 600 }} tickLine={false} axisLine={{ stroke: C.border }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: C.textMuted }} tickLine={false} axisLine={false} tickFormatter={v => `${v}%`} width={36} />
                    <Tooltip content={<EngineTooltip />} cursor={{ fill: C.lightbg }} />
                    <Bar dataKey="visibility" name="Visibility" radius={[4, 4, 0, 0]} fill={C.secondary} />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', gap: '20px', marginTop: '12px', flexWrap: 'wrap' }}>
                  {engineData.map(e => (
                    <div key={e.engineKey} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.textMuted }}>
                      <span style={{
                        width: '10px', height: '10px', borderRadius: '2px',
                        backgroundColor: ENGINE_COLORS[e.engineKey] || C.secondary,
                        display: 'inline-block', flexShrink: 0,
                      }} />
                      {e.engine}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div>
            <SectionLabel>Project access</SectionLabel>
            <ProjectMembersPanel projectId={projectId} apiBaseUrl={apiBaseUrl} isAdmin={isAdmin} />
          </div>
        </div>
    </div>
  );
}
export default ProjectScorecard;
